<template>
  <v-dialog
    v-model="internal"
    max-width="1100"
    :fullscreen="$vuetify.breakpoint.smAndDown"
    persistent
    @keydown.esc="close"
    >
    <v-card>
      <v-toolbar flat dense color="primary" dark>
        <v-toolbar-title>{{ isEdit ? 'Edit WiFi Override' : 'Add WiFi Override' }}</v-toolbar-title>
        <v-spacer/>
        <v-btn icon @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </v-toolbar>

      <v-card-text>
        <v-form ref="form" v-model="valid" lazy-validation>
          <v-row dense>
            <v-col cols="12" md="4">
              <v-text-field
                v-model.trim="form.bssidInput"
                label="BSSID"
                :rules="[rules.required, rules.bssid]"
                hint="12 hex characters (AA11BB22CC33). Colons/dashes will be removed."
                persistent-hint
                clearable
                :disabled="isEdit"
                @blur="normalizeBssid"
              />
            </v-col>
            <v-col cols="6" md="4">
              <v-text-field
                v-model.number="form.lat"
                label="Latitude"
                :rules="[rules.required, rules.lat]"
                type="number" step="0.000001" clearable
              />
            </v-col>
            <v-col cols="6" md="4">
              <v-text-field
                v-model.number="form.lng"
                label="Longitude"
                :rules="[rules.required, rules.lng]"
                type="number" step="0.000001" clearable
              />
            </v-col>
          </v-row>

          <div class="map-wrap mt-2" v-if="mapboxToken">
            <div class="map-header">
              <span>Select on map (drag the pin)</span>
              <v-spacer/>
              <v-btn small text @click="recenterToForm" :disabled="!hasCoords">Center on lat/lng</v-btn>
            </div>
            <div ref="map" class="mapbox"></div>
          </div>
          <v-alert v-else type="info" outlined dense class="mt-4">
            Map is disabled (no Mapbox token). You can still enter lat/lng manually.
          </v-alert>
        </v-form>
      </v-card-text>

      <v-divider/>

      <v-card-actions>
        <v-spacer/>
        <v-btn text :disabled="submitting" @click="close">Cancel</v-btn>
        <v-btn color="primary" :loading="submitting" :disabled="!valid || submitting" @click="onSubmit">
          {{ isEdit ? 'Save' : 'Add' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import mapboxgl from 'mapbox-gl';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import { mapActions } from 'vuex';

export default {
  name: 'AddWifiOverrideModal',
  props: {
    value: { type: Boolean, default: false },        // v-model open
    mode: { type: String, default: 'create' },       // 'create' | 'edit'
    orgId: { type: String, required: true },
    model: { type: Object, default: () => ({ bssid: '', lat: null, lng: null }) },
    mapboxToken: { type: String, default: process.env.VUE_APP_MAPBOX_ACCESS_TOKEN || '' },
    geocoder: { type: Boolean, default: true },
  },
  data() {
    return {
      internal: this.value,
      form: { bssidInput: '', lat: null, lng: null },
      valid: false,
      submitting: false,
      map: null,
      marker: null,
      geocoderCtrl: null,
      rules: {
        required: v => (v !== null && v !== '' && v !== undefined) || 'Required',
        bssid: v => /^[0-9a-fA-F:\-]{12,17}$/.test(v || '') || /^[0-9A-F]{12}$/.test(v || '') || 'Invalid BSSID',
        lat: v => (v >= -90 && v <= 90) || 'Latitude must be between -90 and 90',
        lng: v => (v >= -180 && v <= 180) || 'Longitude must be between -180 and 180',
      }
    };
  },
  computed: {
    isEdit() { return this.mode === 'edit'; },
    hasCoords() { return typeof this.form.lat === 'number' && typeof this.form.lng === 'number'; },
  },
  watch: {
    value(v) {
        this.internal = v;
        if (v) this.initFromModel();
        else this.teardownMap();            // ✅ limpia al cerrar
    },
    internal(v){ this.$emit('input', v); },
  },
  mounted(){ if (this.internal) this.initFromModel(); },
  beforeDestroy(){
    this.teardownMap();
  },
  methods: {
    ...mapActions('wifiOverrides', ['createOverride', 'updateOverride']),
    
    initFromModel(){
      this.form.bssidInput = this.model?.bssid || '';
      this.form.lat = this.isNum(this.model?.lat) ? Number(this.model.lat) : null;
      this.form.lng = this.isNum(this.model?.lng) ? Number(this.model.lng) : null;
      this.$nextTick(() => this.initMap());
    },

    teardownMap() {
        try {
        if (this.geocoderCtrl) {
            this.geocoderCtrl.off('result', this.onGeocoder);
            this.geocoderCtrl = null;
        }
        if (this.map) {
            this.map.remove();
            this.map = null;
        }
        if (this.$refs.map) {
            const olds = this.$refs.map.querySelectorAll('.geocoder-wrap');
            olds.forEach(n => n.remove());   // ✅ elimina inputs duplicados
        }
        this.marker = null;
        } catch (_) {}
    },

    initMap() {
        if (!this.mapboxToken) return;
        mapboxgl.accessToken = this.mapboxToken;

        if (this.map) {
            this.map.remove();
            this.map = null;
        }

        const start = this.hasCoords ? [this.form.lng, this.form.lat] : [-74.006, 40.7128];
        this.map = new mapboxgl.Map({
            container: this.$refs.map,
            style: 'mapbox://styles/mapbox/streets-v12',
            center: start,
            zoom: this.hasCoords ? 14 : 3,
            attributionControl: false,
        });

        if (this.geocoder) {
            // limpiar geocoder previo si existe
            if (this.geocoderCtrl) {
            this.geocoderCtrl.off('result', this.onGeocoder);
            this.geocoderCtrl = null;
            }
            const div = document.createElement('div');
            div.className = 'geocoder-wrap';
            this.$refs.map.appendChild(div);
            this.geocoderCtrl = new MapboxGeocoder({ accessToken: this.mapboxToken, mapboxgl, marker: false });
            this.geocoderCtrl.addTo(div);
            this.geocoderCtrl.on('result', this.onGeocoder);
        }

        this.map.on('load', () => this.placeMarkerFromForm());
        this.map.on('click', (e) => this.setMarker(e.lngLat.lng, e.lngLat.lat, true));
    },
    onGeocoder(e){
      const [lng, lat] = e?.result?.center || [];
      if (this.isNum(lat) && this.isNum(lng)) {
        this.setMarker(lng, lat, true);
        this.map.flyTo({ center: [lng, lat], zoom: 14 });
      }
    },
    placeMarkerFromForm(){
      if (!this.hasCoords) return;
      this.setMarker(this.form.lng, this.form.lat, false);
      this.map.flyTo({ center: [this.form.lng, this.form.lat], zoom: 14 });
    },
    setMarker(lng, lat, updateForm){
      if (!this.marker) {
        this.marker = new mapboxgl.Marker({ draggable: true })
          .setLngLat([lng, lat]).addTo(this.map);
        this.marker.on('dragend', () => {
          const { lng, lat } = this.marker.getLngLat();
          this.form.lat = Number(lat.toFixed(6));
          this.form.lng = Number(lng.toFixed(6));
        });
      } else this.marker.setLngLat([lng, lat]);

      if (updateForm) {
        this.form.lat = Number(lat.toFixed(6));
        this.form.lng = Number(lng.toFixed(6));
      }
    },
    recenterToForm(){ if (this.hasCoords && this.map) this.map.flyTo({ center: [this.form.lng, this.form.lat], zoom: 14 }); },
    isNum(v){ return typeof v === 'number' && isFinite(v); },
    normalizeBssid(){
      const hex = (this.form.bssidInput || '').replace(/[^0-9a-fA-F]/g, '').toUpperCase();
      this.form.bssidInput = hex;
    },
    async onSubmit(){
      console.log('🔵 [Modal] onSubmit started, mode:', this.mode);
      this.normalizeBssid();
      const ok = await this.$refs.form.validate();
      if (!ok) {
        console.log('❌ [Modal] Validation failed');
        return;
      }
      if (!/^[0-9A-F]{12}$/.test(this.form.bssidInput)) {
        console.log('❌ [Modal] Invalid BSSID format');
        this.$toasted.show('BSSID must be 12 hexadecimal characters (e.g., AA11BB22CC33)', { 
          position: 'bottom-right', 
          className: ['toast-error'], 
          duration: 2500 
        });
        return;
      }
      if (!this.hasCoords) { 
        console.log('❌ [Modal] Missing coordinates');
        this.$toasted.show('Latitude/Longitude required', { 
          position: 'bottom-right', 
          className: ['toast-error'], 
          duration: 2500 
        }); 
        return; 
      }

      this.submitting = true;
      try {
        const payload = {
          bssid: this.form.bssidInput,
          lat: Number(this.form.lat),
          lng: Number(this.form.lng),
        };
        
        console.log('🟡 [Modal] Calling Vuex action:', this.mode, payload);
        
        // Call Vuex actions directly instead of emitting
        if (this.mode === 'create') {
          await this.createOverride(payload);
        } else {
          await this.updateOverride(payload);
        }
        
        console.log('✅ [Modal] Action succeeded, closing modal');
        
        // Only close and show success if no error was thrown
        this.$emit('refresh');
        this.$toasted.show(this.isEdit ? 'Override saved' : 'Override added', { 
          position: 'bottom-right', 
          className: ['toast-success'], 
          duration: 1500 
        });
        this.close();
      } catch (e) {
        console.error('❌ [Modal] Error caught:', e.message);
        // Show error but keep modal open
        this.$toasted.show(e?.message || 'Operation failed', { 
          position: 'bottom-right', 
          className: ['toast-error'], 
          duration: 2500 
        });
      } finally { 
        this.submitting = false;
        console.log('🔵 [Modal] onSubmit finished, submitting:', this.submitting);
      }
    },
    close(){ this.internal = false; },
  }
};
</script>

<style scoped>
.map-wrap {
  border: 1px solid #cfd8dc;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
}

.map-header {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  font-size: 14px;
  background: #f5f7f8;
  border-bottom: 1px solid #e0e0e0;
}

.mapbox {
  width: 100%;
  height: 420px;
}

.geocoder-wrap {
  position: absolute;
  z-index: 2;
  top: 10px;
  left: 10px;
  right: 10px;
}

</style>
